import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { buildFaqPage, HOMEPAGE_FAQS } from '../../lib/structuredData';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import WhoWeAreSection from './components/WhoWeAreSection';
import ZeroSection from './components/ZeroSection';
import TrustBridge from './components/TrustBridge';
import SocialProofSection from './components/SocialProofSection';
import HowItWorksSection from './components/HowItWorksSection';
import TabletDemoSection from './components/TabletDemoSection';
import ServiceWarrantySection from './components/ServiceWarrantySection';
// TrustSection ("An open book, down to the last dollar." + hardcoded
// 1,326 / 98.7% / Millions / Hundreds stats + house photo with
// "Industry Leading Warranty Protection" badge) was unrendered
// 2026-05-24 per Mike's call. Component file kept on disk for rollback.
// import TrustSection from './components/TrustSection';
import PricingComparisonSection from './components/PricingComparisonSection';
import RecentInstallsSection from './components/RecentInstallsSection';
import WhyWeBuiltSection from './components/WhyWeBuiltSection';
import Footer from '../../components/Footer';
import IntroAnimation from '../../components/IntroAnimation';
import SanityContentSection from './components/SanityContentSection';
import FAQSection from './components/FAQSection';

const Homepage = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://openbooq.com/#organization",
    "name": "OpenbooQ",
    "url": "https://openbooq.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://openbooq.com/assets/images/Openbooq_blue-1773069231613.png",
      "width": 400,
      "height": 120
    },
    "description": "OpenbooQ is a national online roofing platform that allows homeowners to buy roof replacements online with transparent pricing, no in-home sales visits, and certified local installers.",
    "foundingDate": "2024",
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "sameAs": [
    "https://www.facebook.com/openbooq",
    "https://www.instagram.com/openbooq",
    "https://www.linkedin.com/company/openbooq"],

    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-937-910-8791",
      "contactType": "customer service",
      "availableLanguage": "English",
      "areaServed": "US"
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://openbooq.com/#website",
    "url": "https://openbooq.com",
    "name": "OpenbooQ",
    "description": "Buy your roof online with transparent pricing. No in-home sales visits, no hidden markups.",
    "publisher": {
      "@id": "https://openbooq.com/#organization"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://openbooq.com/blog?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["HomeAndConstructionBusiness", "RoofingContractor"],
    "@id": "https://openbooq.com/#localbusiness",
    "name": "OpenbooQ",
    "url": "https://openbooq.com",
    "telephone": "+1-937-910-8791",
    "logo": "https://openbooq.com/assets/images/Openbooq_blue-1773069231613.png",
    "image": "https://openbooq.com/assets/images/Openbooq_blue-1773069231613.png",
    "description": "National online roofing platform offering transparent roof replacement pricing, certified installers, and premium shingle brands with no in-home sales visits.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 E 4th St",
      "addressLocality": "Dayton",
      "addressRegion": "OH",
      "postalCode": "45402",
      "addressCountry": "US"
    },
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Roofing Services",
      "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Roof Replacement",
          "description": "Full roof replacement with premium shingles and certified installers"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Roof Price",
          "description": "Get an accurate roof replacement price online in under 30 minutes"
        }
      }]

    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "32",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://openbooq.com"
    }]

  };

  // FAQPage built from the visible homepage FAQ section (src/lib/structuredData
  // HOMEPAGE_FAQS mirrors that on-page copy). The core Organization / WebSite /
  // LocalBusiness / Service entity graph is ALSO emitted as static JSON-LD in
  // index.html so non-JS AI crawlers can read it. Duplicate @id nodes are
  // merged by Google; the honest aggregateRating is kept consistent across both.
  const faqSchema = buildFaqPage(HOMEPAGE_FAQS);

  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <IntroAnimation />
      {/* Wrapper bg: dark navy on mobile (matches the hero's --dark-bg
          = #0a1628), white on desktop (the scrolling card-peel surface).
          The previous `bg-transparent` on mobile let the body's white
          show through the ~16px area above the floating header pill
          (header is `position: fixed top-0 pt-4`) and during iOS
          overscroll — read as a grey/empty band above the hero after
          the dark "Professional Installers" micro-bar was removed.
          Matching the hero color erases that gap visually. Wrapper bg
          only paints where descendants don't, so this doesn't affect
          any section below the hero (they all have their own bg). */}
      <div className="min-h-screen bg-[#0a1628] md:bg-white">
        {/* Site header. Renders on ALL viewports — Header.jsx is fully
            responsive on its own (desktop nav inside `hidden lg:flex`,
            mobile hamburger + drawer inside `lg:hidden`). The previous
            `<div className="hidden md:block">` wrapper was a leftover
            from the retired MobileHomeHeroOverlay era (which carried
            its own header). Removing the wrapper restores the hamburger
            + logo on mobile so the page is navigable. */}
        <Header />

        <main>
          {/* Hero — pinned full-viewport on desktop (parallax / scroll-over
              effect), normal flow on mobile. Single source of truth as of
              2026-05-22; the prior MobileHomeHeroOverlay was retired and the
              .hero CSS now has @media (max-width: 768px) rules in tailwind.css. */}
          <div className="md:fixed md:top-0 md:left-0 md:right-0 md:w-full md:overflow-hidden md:h-screen md:z-0">
            <HeroSection />
          </div>

          {/* Scrolling content — slides up over the pinned hero on desktop;
              flows directly after the hero on mobile. Rounded top edge +
              upward shadow are desktop-only (they create the card-peel effect
              against the pinned dark hero behind; on mobile the section just
              butts against the hero with no rounding). */}
          <div className="relative bg-white md:mt-[100vh] md:z-[1] md:rounded-t-[32px] md:shadow-[0_-12px_40px_rgba(0,0,0,0.12)]">
            <TabletDemoSection />
            {/* Color blend band — smooth white → ink transition so the
                bottom of TabletDemoSection (white) eases into the top of
                ZeroSection (navy) instead of meeting with a hard seam.
                Decorative only, no content. Defined in tailwind.css under
                `.section-transition-white-to-ink` (sibling to TrustBridge,
                which does the reverse navy → white transition lower down). */}
            <div className="section-transition-white-to-ink" aria-hidden="true" />
            <ZeroSection />
            <TrustBridge />
            <SocialProofSection />
            <WhoWeAreSection />
            <PricingComparisonSection />
            <RecentInstallsSection />
            <WhyWeBuiltSection />
            <HowItWorksSection />
            <ServiceWarrantySection />
            <FAQSection />
            <SanityContentSection />
            <Footer />
          </div>
        </main>
      </div>
    </>);

};

export default Homepage;